import { faBook } from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faFolder } from '@fortawesome/free-solid-svg-icons/faFolder';
import { faWind } from '@fortawesome/free-solid-svg-icons/faWind';
import { faVolcano } from '@fortawesome/pro-light-svg-icons';
import Site from 'es/app/business-logic/domain-models/Site';
import { NavDrawerHeader } from 'es/app/components/layout/nav/NavDrawerHeader';
import KnownAccessControls, { RequiredAccessControls } from 'es/app/security/KnownAccessControls';
import { useUserStore } from 'es/app/store/GlobalStore';
import { useSiteRouteMap } from 'es/app/views/site/useSiteRouteMap';
import SystemRouteMap from 'es/app/views/system/RouteMap';
import FacilitiesIcon from 'es/app/visuals/images/facilities-icon.svg?react';
import withSuspense from 'es/core/hocs/withSuspense';
import { useI18n } from 'es/core/hooks/useI18n';
import { observer } from 'mobx-react';
import styles from './NavDrawer.module.css';
import { INavDrawerLink } from './NavDrawerLink';
import { NavDrawerLinkList } from './NavDrawerLinkList';

const NavDrawer = withSuspense(
    observer(() => {
        const { l10n } = useI18n('app');
        const { activeSite } = useUserStore();

        const facilityItems = useFacilityDrawerItems();
        const systemItems = useSystemDrawerItems();

        return (
            <aside className={styles.navDrawer}>
                <NavDrawerHeader />

                <div className={styles.navDrawerLinkContainer}>
                    <NavDrawerLinkList items={facilityItems} />
                    <NavDrawerLinkList items={systemItems} />
                </div>
            </aside>
        );

        ////////////////////

        function useFacilityDrawerItems(): INavDrawerLink[] {
            const routeMap = useSiteRouteMap();

            return !activeSite
                ? []
                : [
                      {
                          route: routeMap.processes.root,
                          text: l10n('navigation.processes.label'),
                          icon: ['fal', 'router'],
                          accessControls: KnownAccessControls.api.company.processes.read,
                      },
                      {
                          route: routeMap.reports.search(),
                          text: l10n('navigation.reports.label'),
                          icon: ['fad', 'chart-line'],
                          accessControls: KnownAccessControls.api.company.reportDefinitions.read,
                      },
                      {
                          route: routeMap.alerts.search(),
                          text: l10n('navigation.alerts.label'),
                          icon: ['fad', 'siren-on'],
                          accessControls: RequiredAccessControls.alerts,
                      },
                      {
                          route: routeMap.sequences.search(),
                          text: l10n('navigation.sequence.label'),
                          icon: ['fad', 'list-ol'],
                          accessControls: KnownAccessControls.api.company.sequences.read,
                      },
                      {
                          route: routeMap.dataImporters.search(),
                          text: l10n('navigation.dataImporters.label'),
                          icon: ['fad', 'file-import'],
                          accessControls: KnownAccessControls.api.company.sequences.read,
                      },
                      {
                          route: routeMap.models.search(),
                          text: l10n('navigation.modelling.label'),
                          icon: faWind,
                          accessControls: KnownAccessControls.api.company.sequences.read,
                      },
                      {
                          route: routeMap.emissionSources.search(),
                          text: l10n('navigation.emissionSources.label'),
                          icon: faVolcano,
                          accessControls: KnownAccessControls.api.company.sequences.read,
                      },
                      {
                          route: routeMap.files.search(),
                          text: l10n('navigation.files.label'),
                          icon: faFolder,
                      },
                      {
                          route: routeMap.temporalThresholds.search(),
                          text: l10n('navigation.temporalThresholds.label'),
                          icon: faClock,
                      },
                  ];
        }

        function useSystemDrawerItems(): INavDrawerLink[] {
            return [
                {
                    route: SystemRouteMap.library.search(),
                    text: l10n('navigation.library.label'),
                    icon: faBook,
                },
                {
                    route: SystemRouteMap.people.search(),
                    text: l10n('navigation.people.label'),
                    icon: ['fad', 'users'],
                    accessControls: KnownAccessControls.api.system.users.read,
                },
                {
                    route: SystemRouteMap.facilities.search(),
                    text: l10n('navigation.facilities.label'),
                    icon: <FacilitiesIcon />,
                    accessControls: KnownAccessControls.api.system.companies.read,
                    site: Site.Global,
                },
                {
                    route: SystemRouteMap.accessGroups.search(),
                    text: l10n('navigation.accessGroups.label'),
                    icon: ['far', 'shield-alt'],
                    accessControls: KnownAccessControls.api.system.accessGroups.read,
                    site: Site.Global,
                },
            ];
        }
    })
);

export default NavDrawer;
